import { type GridSelectionModel } from '@mui/x-data-grid-pro';
import { createSelector } from 'reselect';

import type Order from '~/types/Order';

import { type CheckoutRatesAfterPoll } from '~/reducers/orders/orders.reducer';
import { type ApplicationState } from '~/store';
import { type OrderSelection } from '~/types/Order';

const EMPTY_ORDER = {
  partialFulfillmentRequests: [],
};

const getOrdersStore = (state: ApplicationState) => state.orders;

const getOrderIdFromProps = (_: unknown, props: { orderId: string }) => props.orderId;
const getOrderObjectIdFromProps = (_: unknown, props: { orderObjectId: string }) =>
  props.orderObjectId;
const getOrderObjectIdsFromProps = (_: unknown, props: { orderObjectIds: readonly string[] }) =>
  props.orderObjectIds;

export const getOrderAction = createSelector(
  getOrdersStore,
  (ordersStore) => ordersStore.orderAction,
);

export const getHasNewOrders = createSelector(
  getOrdersStore,
  (ordersStore): boolean => ordersStore.hasNewOrders,
);

export const getOrdersCount = createSelector(
  getOrdersStore,
  (orderStore) => orderStore.totalOrders,
);

const getOrdersAllIds = createSelector(
  getOrdersStore,
  (ordersStore): readonly string[] => ordersStore.allIds,
);

export const getOrdersData = createSelector(
  getOrdersStore,
  (ordersStore): { [orderId: string]: Order } => ordersStore.data,
);

export const getOrders = createSelector(
  getOrdersAllIds,
  getOrdersData,
  (allIds, ordersData): Order[] => allIds.map((id) => ordersData[id]).filter((v) => v),
);

export const getTotalOrders = createSelector(
  getOrdersStore,
  (ordersStore): number => ordersStore.totalOrders,
);

export const getAllOrdersCount = createSelector(
  getOrdersStore,
  (ordersStore): number => ordersStore.allOrdersCount,
);

export const getHasOrderFetchError = createSelector(
  getOrdersStore,
  (ordersStore): boolean => ordersStore.fetchOrderHasError,
);

export const getIsOrderLoading = createSelector(
  getOrdersStore,
  (ordersStore): boolean => ordersStore.isLoadingOrder,
);

export const getIsLoading = createSelector(
  getOrdersStore,
  (ordersStore): boolean => ordersStore.isLoading,
);

export const getIsCreatingReturnBatchEntry = createSelector(
  getOrdersStore,
  (ordersStore): boolean => ordersStore.isCreatingReturnBatchEntry,
);

export const getCreateReturnBatchEntryHasError = createSelector(
  getOrdersStore,
  (ordersStore): boolean => ordersStore.createReturnBatchEntryHasError,
);

export const getReturnLabelCreatedSuccessfully = createSelector(
  getOrdersStore,
  (ordersStore): boolean => ordersStore.returnLabelCreatedSuccessfully,
);

export const getLastOrderId = createSelector(
  getOrdersStore,
  (ordersStore): string => ordersStore.lastOrderId,
);

export const getSelectedOrders = createSelector(
  getOrdersStore,
  (ordersStore): OrderSelection => ordersStore.selectedOrders,
);

export const getCheckedOrders = createSelector(
  getOrdersStore,
  (ordersStore): OrderSelection => ordersStore.checkedOrders,
);

export const getRowModelBeingEdited = createSelector(
  getOrdersStore,
  (ordersStore): GridSelectionModel => ordersStore.rowModelBeingEdited,
);

export const getOrdersRemainingForLabelDownload = createSelector(
  getOrdersStore,
  (ordersStore): OrderSelection => ordersStore.ordersRemainingLabelDownload,
);

export const getCheckoutRateAfterPoll = createSelector(
  getOrdersStore,
  (ordersStore): Maybe<CheckoutRatesAfterPoll> => ordersStore.checkoutRateAfterPoll,
);

export const getSelectedOrdersData = createSelector(
  getSelectedOrders,
  getOrdersData,
  (ordersSelection, ordersData): Order[] =>
    Object.keys(ordersSelection || {})
      .map((id) => ordersData[id])
      .filter((v) => v),
);

export const makeGetOrderById = () =>
  createSelector(
    getOrdersData,
    getOrderIdFromProps,
    (ordersData, orderId: string): Order => ordersData[orderId],
  );

export const getOrderByObjectId = createSelector(
  getOrdersData,
  getOrderObjectIdFromProps,
  (ordersData, orderObjectId): Order => ordersData[orderObjectId] || EMPTY_ORDER,
);

export const getOrderById = createSelector(
  getOrdersData,
  getOrderIdFromProps,
  (ordersData, orderId: string): Order => ordersData[orderId],
);

export const getIsUpdatingOrder = createSelector(
  getOrdersStore,
  (ordersStore): boolean => ordersStore.isUpdatingBatchEntry,
);

export const getIsSingleOrderMode = createSelector(
  getOrdersStore,
  (ordersStore): boolean => ordersStore.isSingleOrderMode,
);

export const getSelectedCount = createSelector(getSelectedOrders, (selectedOrders): number =>
  Object.values(selectedOrders).reduce(
    (count, currVal) => (currVal.length ? count + currVal.length : count + 1),
    0,
  ),
);

export const getPatchBatchEntriesExtrasRequest = createSelector(
  getOrdersStore,
  (orderStore) => orderStore.patchBatchEntriesExtrasRequest,
);

export const getPatchBatchEntriesRequest = createSelector(
  getOrdersStore,
  (orderStore) => orderStore.patchBatchEntriesRequest,
);

const getOrdersDataByObjectIds = createSelector(
  getOrdersData,
  getOrderObjectIdsFromProps,
  (ordersData, orderObjectIds) => orderObjectIds.map((objectId) => ordersData[objectId]),
);

// Returns all successful transactions for each order
export const getOrderSuccessfulTransactionIds = createSelector(
  getOrdersDataByObjectIds,
  (ordersData): string[] => {
    // filter on orders that have successful transactions
    const ordersWithTransactions = ordersData.filter(
      (order) => order?.transactions?.some((transaction) => transaction.status === 'SUCCESS'),
    );

    return ordersWithTransactions.reduce((txnIds: string[], order: Order) => {
      const orderTxns = order.transactions
        .filter((t) => t.status === 'SUCCESS')
        .map((t) => t.objectId);
      return [...txnIds, ...orderTxns];
    }, []);
  },
);

export const getIsUpdatingCustomsItems = createSelector(
  getOrdersStore,
  (ordersStore) => ordersStore.isUpdatingCustomsItems,
);

export const getIsCreatingCustomsItems = createSelector(
  getOrdersStore,
  (ordersStore) => ordersStore.isCreatingCustomsItems,
);

export const getIsSidePanelCheckout = createSelector(
  getOrdersStore,
  (ordersStore) => ordersStore.isSidePanelCheckout,
);

export const getIsParcelUpdating = createSelector(
  getOrdersStore,
  (ordersStore) => ordersStore.isUpdatingParcel,
);

export const getAutoSaveErrors = (orderSelection: OrderSelection) =>
  createSelector(
    getOrdersStore,
    (ordersStore) =>
      !!Object.keys(orderSelection).find((orderId) => ordersStore.autoSaveErrors[orderId]),
  );

export const getIsLoadingOrderTags = createSelector(
  getOrdersStore,
  (ordersStore) => ordersStore.isLoadingOrderTags,
);

export const getOrderTagsBeingUpdated = createSelector(
  getOrdersStore,
  (ordersStore) => ordersStore.orderTagsBeingUpdated,
);
